import {
  prisma,
  type Prisma,
  ResourceType,
  ActionType,
  FormEntityType,
} from "client";
import {
  DEFAULT_QUESTIONS,
  toStorableFormQuestion,
} from "@sajilni/utils/formTemplate";
import { getLanguages } from "services/language";

// GET
export const getContactGroupById = async (userId: string, id: string) => {
  try {
    const contactGroup = await prisma.contactBookGroup.findFirst({
      where: {
        id: id,
      },
      include: {
        contacts: true,
        fields: {
          include: { formTemplate: true },
        },
      },
    });

    return contactGroup;
  } catch (e: any) {
    return e;
  }
};

export const getContactBookGroupByWhiteLabelId = async (
  userId: string,
  whiteLabelId: string
) => {
  try {
    const contactGroups = await prisma.contactBookGroup.findMany({
      where: {
        whiteLabelId: whiteLabelId,
      },
      include: {
        event: true,
        contacts: true,
      },
    });

    return contactGroups;
  } catch (e: any) {
    return e;
  }
};
export const getContactBookGroupByExhibitorId = async (exhibitorId: string) => {
  try {
    const contactGroups = await prisma.contactBookGroup.findFirst({
      where: {
        exhibitorId: exhibitorId,
      },
      include: {
        event: true,
        fields: {
          include: { formTemplate: true },
        },
      },
    });

    return contactGroups;
  } catch (e: any) {
    return e;
  }
};
export const getContactBookGroupBySponsorId = async (sponsorId: string) => {
  try {
    const contactGroups = await prisma.contactBookGroup.findFirst({
      where: {
        sponsorId: sponsorId,
      },
      include: {
        event: true,
        fields: {
          include: { formTemplate: true },
        },
      },
    });

    return contactGroups;
  } catch (e: any) {
    return e;
  }
};
// export const getContactBookGroupByEventId = async (
//   eventId: string,
//   page: number = 1,
//   pageSize: number = 10
// ) => {
//   const skip = (page - 1) * pageSize;
//   const take = pageSize;

//   const contactGroups = await prisma.contactBookGroup.findMany({
//     where: {
//       eventIds: {
//         hasSome: eventId,
//       },
//     },
//     include: {
//       events: true,
//       contacts: {
//         skip: skip,
//         take: take,
//       },
//       _count: {
//         select: {
//           contacts: true,
//         },
//       },
//     },
//     orderBy: {
//       id: "desc",
//     },
//   });

//   const contactGroupsWithPagination = contactGroups.map(group => {
//     const totalContacts = group._count.contacts;
//     const hasNextPage = skip + take < totalContacts;
//     return {
//       ...group,
//       hasNextPage
//     };
//   });

//   return {
//     contactGroups: contactGroupsWithPagination
//   };
// };
export const getContactBookGroupByEventId = async (
  userId: string,
  eventId: string,
  search?: string
) => {
  try {
    const contactGroups = await prisma.contactBookGroup.findMany({
      where: {
        eventId,
      },
      include: {
        fields: {
          include: { formTemplate: true },
        },
        contacts: true,
        _count: {
          select: {
            contacts: true,
          },
        },
      },
      orderBy: {
        id: "desc",
      },
    });

    return contactGroups;
  } catch (e: any) {
    return e;
  }
};
export const getContactBookGroupByInvitationId = async (
  userId: string,
  invitationId: string,
  search?: string
) => {
  try {
    const contactGroups = await prisma.contactBookGroup.findMany({
      where: {
        invitationAttendeeCategories: {
          some: {
            invitationId: invitationId,
          },
        },
      },
      include: {
        fields: {
          include: { formTemplate: true },
        },
        contacts: true,
        _count: {
          select: {
            contacts: true,
          },
        },
      },
      orderBy: {
        id: "desc",
      },
    });

    return contactGroups;
  } catch (e: any) {
    return e;
  }
};
// POST
export const createContactBookGroup = async (
  userId: string,
  contactGroupPayload: Prisma.ContactBookGroupCreateInput,
  whiteLabelId: string,
  fields: Array<{ name: string; label: string; type: string }>,
  eventId: string,
  contactBookIds?: string[],
  invitationId?: string
) => {
  try {
    const languages = await getLanguages();

    // Aggregate all fields into a single JSON object
    const jsonData = {
      title: "",
      logoPosition: "",
      pages: [
        {
          name: "page1",
          elements: fields.map((field) => {
            const existingQuestion = DEFAULT_QUESTIONS.find(
              (q) => q.name === field.name
            );

            // If the question exists, use its questionJSON, otherwise create a default element
            const element = existingQuestion ?? {
              type: field.type,
              name: field.name,
              title: field.label,
            };
            return toStorableFormQuestion(
              element as Record<string, unknown>
            ) as typeof element;
          }),
        },
      ],
    };

    const formattedFields = {
      type: FormEntityType.CONTACT_BOOK_GROUP,
      formTemplate: {
        create: {
          translations: {
            create: languages.map((lang) => ({
              name: `${contactGroupPayload.name} Form`,
              language: { connect: { id: lang.id } },
            })),
          },
          whiteLabelId,
          createdById: userId,
          json: jsonData as Prisma.InputJsonValue, // ✅ Explicitly cast to Prisma.InputJsonValue
          text: JSON.stringify(jsonData),
          numberOfQuestions: fields.length,
        },
      },
    };

    const contactGroup = await prisma.$transaction(async (tx) => {
      const group = await tx.contactBookGroup.create({
        data: {
          ...contactGroupPayload,
          fields: {
            create: formattedFields,
          },
          contacts: contactBookIds?.length
            ? { connect: contactBookIds.map((id) => ({ id })) }
            : undefined,
          whiteLabel: whiteLabelId
            ? { connect: { id: whiteLabelId } }
            : undefined,
          event: { connect: { id: eventId } },
        },
        include: {
          fields: {
            include: { formTemplate: true },
          },
        },
      });

      if (invitationId) {
        const userExists = await tx.user.findUnique({
          where: { id: userId },
          select: { id: true },
        });

        await tx.invitationAttendeeCategory.create({
          data: {
            invitationId,
            contactBookGroupId: group.id,
            ...(userExists ? { createdById: userId } : {}),
          },
        });
      }

      return group;
    });
    return contactGroup;
  } catch (e: any) {
    console.error("Error creating ContactBookGroup:", e);
    throw new Error(e.message || "Failed to create contact book group");
  }
};
export const updateContactBookGroup = async (
  userId: string,
  id: string,
  updatedData: Prisma.ContactBookGroupUpdateInput,
  fields: Array<{ name: string; label: string; type: string }>,
  invitationId?: string
) => {
  try {
    const languages = await getLanguages();

    const jsonData = {
      title: "",
      logoPosition: "",
      pages: [
        {
          name: "page1",
          elements: fields.map((field) => {
            const existingQuestion = DEFAULT_QUESTIONS.find(
              (q) => q.name === field.name
            );

            // If the question exists, use its questionJSON, otherwise create a default element
            const element = existingQuestion ?? {
              type: field.type,
              name: field.name,
              title: field.label,
            };
            return toStorableFormQuestion(
              element as Record<string, unknown>
            ) as typeof element;
          }),
        },
      ],
    };

    const formattedFields = {
      formTemplate: {
        update: {
          translations: {
            create: languages.map((lang) => ({
              name: `${updatedData.name} Form`,
              language: { connect: { id: lang.id } },
            })),
          },

          json: jsonData as Prisma.InputJsonValue,
          text: JSON.stringify(jsonData),
          numberOfQuestions: fields.length,
        },
      },
    };

    const contactBookGroup = await prisma.contactBookGroup.update({
      where: {
        id,
      },
      data: {
        ...updatedData,
        fields: {
          update: formattedFields,
        },
        invitationAttendeeCategories: invitationId
          ? {
              create: {
                invitation: {
                  connect: { id: invitationId },
                },
              },
            }
          : undefined,
      },
      include: {
        fields: {
          include: { formTemplate: true },
        },
      },
    });

    return contactBookGroup;
  } catch (e: any) {
    console.error(e);
    throw new Error(e);
  }
};
export const deleteContactBookGroup = async (
  id: string,
  invitationId?: string
) => {
  try {
    let contactGroup, contacts;

    if (invitationId) {
      contactGroup = await prisma.contactBookGroup.update({
        where: { id },
        data: {
          invitationAttendeeCategories: {
            deleteMany: [{ invitationId: invitationId }],
          },
        },
      });
    } else {
      await prisma.formEntity.deleteMany({
        where: {
          entityID: id,
          type: FormEntityType.CONTACT_BOOK_GROUP,
        },
      });
      await prisma.profile.deleteMany({
        where: { contactBookGroupId: id },
      });
      contactGroup = await prisma.contactBookGroup.delete({
        where: { id },
      });
    }
    return contactGroup;
  } catch (e: any) {
    console.error(e);
    throw new Error(e);
  }
};

export const duplicateContactGroup = async (
  contactGroupById: string,
  duplicateContactGroupById: string,
  eventId: string
) => {
  try {
    // return duplicateContactGroupById;
    const existingContactBook = await prisma.contactBookGroup.findFirst({
      where: {
        id: contactGroupById,
      },
      include: {
        contacts: true,
        fields: {
          include: { formTemplate: true },
        },
      },
    });
    if (!existingContactBook) {
      throw new Error("Contact group not found");
    }

    if (existingContactBook.contacts.length > 0) {
      await prisma.profile.createMany({
        data: existingContactBook.contacts.map((profile) => ({
          ...profile,
          id: undefined,
          eventId,
          contactBookGroupId: duplicateContactGroupById,
        })),
      });
    }

    return existingContactBook;
  } catch (e: any) {
    console.error(e);
    throw new Error(e);
  }
};

export const copyContactGroup = async (
  userId: string,
  contactGroupId: string,
  invitationId: string
) => {
  try {
    const existingContactBook = await prisma.contactBookGroup.findFirst({
      where: {
        id: contactGroupId,
      },
      include: {
        contacts: true,
        fields: {
          include: { formTemplate: true },
        },
      },
    });
    if (!existingContactBook) {
      throw new Error("Contact group not found");
    }

    const existingLink = await prisma.invitationAttendeeCategory.findFirst({
      where: {
        contactBookGroupId: contactGroupId,
        invitationId,
      },
    });

    if (existingLink) {
      throw new Error(
        "This contact group is already linked to the invitation."
      );
    }

    // Create InvitationAttendeeCategory
    const attendeeCategory = await prisma.invitationAttendeeCategory.create({
      data: {
        contactBookGroupId: contactGroupId,
        invitationId,
        createdById: userId,
      },
    });

    return attendeeCategory;
  } catch (e: any) {
    console.error(e);
    throw new Error(e);
  }
};
